/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate shortcut titles/descriptions (debt) */
/**
 * Default command catalogue and key map for Listto.
 *
 * Commands are declared without `run` handlers — features bind their own
 * runtime behaviour through `useCommandHandler`. The keymap is the single
 * source of truth that the help modal and profile/shortcuts page consume.
 *
 * Title and description strings use plain English here so the Lingui
 * extractor can pick them up directly via `t()` at render time in the UI.
 */

import type { Command, KeyBinding } from "@fleet/shortcuts";

export const COMMAND_IDS = {
  PALETTE_OPEN: "palette.open",
  PALETTE_CLOSE: "palette.close",
  PALETTE_NEXT: "palette.next",
  PALETTE_PREV: "palette.prev",
  PALETTE_CONFIRM: "palette.confirm",
  PALETTE_CYCLE_FILTER: "palette.cycle-filter",

  HELP_OPEN: "help.open",
  HELP_CLOSE: "help.close",

  // PLUGIN-KERNEL: the id stays here (it is the app-wide vocabulary the
  // handlers use), but the command and its binding are CONTRIBUTED by the
  // activity plugin — see `kernel/composition.ts`. An app without the feature
  // no longer advertises Mod+Z in the help modal.
  UNDO: "app.undo",

  NEW_TASK_FOCUS: "task.focus-input",
  NEW_LIST_OPEN: "list.create",
  NEW_GROUP_OPEN: "group.create",

  UI_TOGGLE_SIDEBAR: "ui.toggle-sidebar",
  UI_TOGGLE_BLUR: "ui.toggle-blur",
  UI_TOGGLE_FOLLOW_DEVICE_THEME: "ui.toggle-follow-device-theme",
  UI_TOGGLE_BACKGROUND_STYLE: "ui.toggle-background-style",

  GOTO_INBOX: "nav.goto-inbox",
  GOTO_MY_DAY: "nav.goto-my-day",
  GOTO_PLANNED: "nav.goto-planned",
  GOTO_IMPORTANT: "nav.goto-important",
  GOTO_ALL: "nav.goto-all",
  GOTO_DASHBOARD: "nav.goto-dashboard",
  GOTO_PROFILE: "nav.goto-profile",
  GOTO_THEMES: "nav.goto-themes",
  GOTO_SHORTCUTS: "nav.goto-shortcuts",
  GOTO_LANGUAGE: "nav.goto-language",

  LIST_NEXT: "nav.next-list",
  LIST_PREV: "nav.prev-list",

  TODO_FOCUS_NEXT: "todo.focus-next",
  TODO_FOCUS_PREV: "todo.focus-prev",
  TODO_FOCUS_FIRST: "todo.focus-first",
  TODO_FOCUS_LAST: "todo.focus-last",
  TODO_OPEN: "todo.open-detail",
  TODO_CLEAR_FOCUS: "todo.clear-focus",
  TODO_TOGGLE_COMPLETE: "todo.toggle-complete",
  TODO_TOGGLE_IMPORTANT: "todo.toggle-important",
  TODO_ADD_TO_MY_DAY: "todo.add-to-my-day",
  TODO_DELETE: "todo.delete",
  TODO_MOVE_UP: "todo.move-up",
  TODO_MOVE_DOWN: "todo.move-down",
  TODO_DUPLICATE: "todo.duplicate",
  TODO_MOVE_TO_LIST: "todo.move-to-list",

  SELECTION_TOGGLE: "selection.toggle-mode",
  SELECTION_SELECT_ALL: "selection.select-all",

  DRAWER_CLOSE: "drawer.close",
  DRAWER_DELETE: "drawer.delete",
  DRAWER_TOGGLE_IMPORTANT: "drawer.toggle-important",
  DRAWER_ADD_TO_MY_DAY: "drawer.add-to-my-day",
  DRAWER_MOVE_TO_LIST: "drawer.move-to-list",
  DRAWER_SAVE_CLOSE: "drawer.save-close",
  DRAWER_FOCUS_TITLE: "drawer.focus-title",
  DRAWER_OPEN_DATE: "drawer.open-date",
  DRAWER_OPEN_REMINDER: "drawer.open-reminder",
  DRAWER_OPEN_RECURRENCE: "drawer.open-recurrence",
  DRAWER_OPEN_NOTES: "drawer.open-notes",
  DRAWER_OPEN_SUBTASK: "drawer.open-subtask",
  DRAWER_OPEN_ATTACHMENT: "drawer.open-attachment",
  DRAWER_SCROLL_UP: "drawer.scroll-up",
  DRAWER_SCROLL_DOWN: "drawer.scroll-down",

  DIALOG_CONFIRM: "dialog.confirm",
  DIALOG_CLOSE: "dialog.close",
} as const;

export const defaultCommands: readonly Command[] = [
  // ── Palette ─────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.PALETTE_OPEN,
    category: "Global Actions",
    titleKey: "Search tasks or lists",
  },
  {
    id: COMMAND_IDS.PALETTE_CLOSE,
    category: "Global Actions",
    titleKey: "Close active panels or dialogs",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_NEXT,
    category: "Global Actions",
    titleKey: "Move focus down in palette",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_PREV,
    category: "Global Actions",
    titleKey: "Move focus up in palette",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_CONFIRM,
    category: "Global Actions",
    titleKey: "Open selected result",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_CYCLE_FILTER,
    category: "Global Actions",
    titleKey: "Cycle palette filter",
    when: "palette.open",
  },

  // ── Help ────────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.HELP_OPEN,
    category: "Global Actions",
    titleKey: "Open keyboard shortcuts help",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.HELP_CLOSE,
    category: "Global Actions",
    titleKey: "Close keyboard shortcuts help",
  },

  // ── Tasks ───────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.NEW_TASK_FOCUS,
    category: "Global Actions",
    titleKey: "Focus new task input",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.NEW_LIST_OPEN,
    category: "Global Actions",
    titleKey: "Create new custom list",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.NEW_GROUP_OPEN,
    category: "Global Actions",
    titleKey: "Create new list group",
    when: "!input.focused && !palette.open && !dialog.open",
  },

  // ── UI toggles ──────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.UI_TOGGLE_SIDEBAR,
    category: "Global Actions",
    titleKey: "Toggle sidebar",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BLUR,
    category: "Global Actions",
    titleKey: "Toggle blur effect",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME,
    category: "Global Actions",
    titleKey: "Toggle follow-device theme",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE,
    category: "Global Actions",
    titleKey: "Toggle background style (gradients / lines)",
  },

  // ── Navigation chords ──────────────────────────────────────────────────
  {
    id: COMMAND_IDS.GOTO_INBOX,
    category: "Quick Views (Go To)",
    titleKey: "Go to Inbox",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_MY_DAY,
    category: "Quick Views (Go To)",
    titleKey: "Go to My Day",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_PLANNED,
    category: "Quick Views (Go To)",
    titleKey: "Go to Planned",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_IMPORTANT,
    category: "Quick Views (Go To)",
    titleKey: "Go to Important",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_ALL,
    category: "Quick Views (Go To)",
    titleKey: "Go to All Tasks",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_DASHBOARD,
    category: "Quick Views (Go To)",
    titleKey: "Go to Dashboard",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_PROFILE,
    category: "Quick Views (Go To)",
    titleKey: "Go to Profile / Settings",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_THEMES,
    category: "Quick Views (Go To)",
    titleKey: "Go to Themes gallery",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_SHORTCUTS,
    category: "Quick Views (Go To)",
    titleKey: "Go to Shortcuts page",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.GOTO_LANGUAGE,
    category: "Quick Views (Go To)",
    titleKey: "Go to Language settings",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },

  // ── List navigation (left/right) ───────────────────────────────────────
  {
    id: COMMAND_IDS.LIST_NEXT,
    category: "List Navigation",
    titleKey: "Next list",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.LIST_PREV,
    category: "List Navigation",
    titleKey: "Previous list",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },

  // ── Focused-todo actions ───────────────────────────────────────────────
  {
    id: COMMAND_IDS.TODO_FOCUS_NEXT,
    category: "List Navigation",
    titleKey: "Move focus down between tasks",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_FOCUS_PREV,
    category: "List Navigation",
    titleKey: "Move focus up between tasks",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_FOCUS_FIRST,
    category: "List Navigation",
    titleKey: "Focus first task",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_FOCUS_LAST,
    category: "List Navigation",
    titleKey: "Focus last task",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_OPEN,
    category: "List Navigation",
    titleKey: "Open selected task details",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_CLEAR_FOCUS,
    category: "List Navigation",
    titleKey: "Clear task selection / focus",
    when: "todo.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_TOGGLE_COMPLETE,
    category: "Task Management (Focused)",
    titleKey: "Complete / Uncomplete task",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_TOGGLE_IMPORTANT,
    category: "Task Management (Focused)",
    titleKey: "Star task / Mark as important",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_DELETE,
    category: "Task Management (Focused)",
    titleKey: "Delete selected task",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_ADD_TO_MY_DAY,
    category: "Task Management (Focused)",
    titleKey: "Add task to / remove from My Day",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_MOVE_UP,
    category: "Task Management (Focused)",
    titleKey: "Move task up",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_MOVE_DOWN,
    category: "Task Management (Focused)",
    titleKey: "Move task down",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_DUPLICATE,
    category: "Task Management (Focused)",
    titleKey: "Duplicate task",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.TODO_MOVE_TO_LIST,
    category: "Task Management (Focused)",
    titleKey: "Move task to another list",
    when: "todo.focused && !input.focused && !palette.open && !dialog.open",
  },

  // ── Selection mode ─────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.SELECTION_TOGGLE,
    category: "Selection",
    titleKey: "Enter / exit multi-select mode",
    when: "!input.focused && !palette.open && !dialog.open && !drawer.open",
  },
  {
    id: COMMAND_IDS.SELECTION_SELECT_ALL,
    category: "Selection",
    titleKey: "Select all tasks in the current list",
    when: "selection.active && !input.focused && !palette.open && !dialog.open",
  },

  // ── Drawer ──────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.DRAWER_CLOSE,
    category: "Task Details (Drawer)",
    titleKey: "Close task details",
    when: "drawer.open",
  },
  {
    id: COMMAND_IDS.DRAWER_DELETE,
    category: "Task Details (Drawer)",
    titleKey: "Delete task",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_FOCUS_TITLE,
    category: "Task Details (Drawer)",
    titleKey: "Focus task title",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_DATE,
    category: "Task Details (Drawer)",
    titleKey: "Set due date",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_REMINDER,
    category: "Task Details (Drawer)",
    titleKey: "Set reminder",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_RECURRENCE,
    category: "Task Details (Drawer)",
    titleKey: "Set recurrence",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_NOTES,
    category: "Task Details (Drawer)",
    titleKey: "Focus notes",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_SUBTASK,
    category: "Task Details (Drawer)",
    titleKey: "Add subtask",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_OPEN_ATTACHMENT,
    category: "Task Details (Drawer)",
    titleKey: "Add attachment",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_TOGGLE_IMPORTANT,
    category: "Task Details (Drawer)",
    titleKey: "Star task / Mark as important",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_ADD_TO_MY_DAY,
    category: "Task Details (Drawer)",
    titleKey: "Add / remove task from My Day",
    when: "drawer.open && !input.focused",
  },
  {
    id: COMMAND_IDS.DRAWER_MOVE_TO_LIST,
    category: "Task Details (Drawer)",
    titleKey: "Move task to another list",
    when: "drawer.open",
  },
  {
    id: COMMAND_IDS.DRAWER_SAVE_CLOSE,
    category: "Task Details (Drawer)",
    titleKey: "Save and close drawer",
    when: "drawer.open",
  },

  // ── Dialogs ─────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.DIALOG_CONFIRM,
    category: "Dialogs",
    titleKey: "Confirm dialog",
    when: "dialog.open",
  },
  {
    id: COMMAND_IDS.DIALOG_CLOSE,
    category: "Dialogs",
    titleKey: "Close dialog",
    when: "dialog.open",
  },
] as const;

export const defaultKeymap: readonly KeyBinding[] = [
  // ── Palette ─────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.PALETTE_OPEN, keys: "Mod+k" },
  { command: COMMAND_IDS.PALETTE_CLOSE, keys: "Escape", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_NEXT, keys: "ArrowDown", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_PREV, keys: "ArrowUp", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_CONFIRM, keys: "Enter", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_CYCLE_FILTER, keys: "Tab", scope: "palette" },

  // ── Help ────────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.HELP_OPEN, keys: "?" },

  // ── Tasks ───────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.NEW_TASK_FOCUS, keys: "n" },
  { command: COMMAND_IDS.NEW_LIST_OPEN, keys: "Alt+n" },
  { command: COMMAND_IDS.NEW_GROUP_OPEN, keys: "Alt+Shift+n" },

  // ── UI toggles ──────────────────────────────────────────────────────────
  { command: COMMAND_IDS.UI_TOGGLE_SIDEBAR, keys: "Mod+b" },
  { command: COMMAND_IDS.UI_TOGGLE_BLUR, keys: "Mod+Shift+b" },
  { command: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, keys: "Mod+Shift+d" },
  // UI_TOGGLE_BACKGROUND_STYLE intentionally has no default binding.

  // ── Navigation chords ──────────────────────────────────────────────────
  { command: COMMAND_IDS.GOTO_INBOX, keys: "g i" },
  { command: COMMAND_IDS.GOTO_MY_DAY, keys: "g t" },
  { command: COMMAND_IDS.GOTO_MY_DAY, keys: "g d" },
  { command: COMMAND_IDS.GOTO_PLANNED, keys: "g p" },
  { command: COMMAND_IDS.GOTO_IMPORTANT, keys: "g m" },
  { command: COMMAND_IDS.GOTO_ALL, keys: "g a" },
  { command: COMMAND_IDS.GOTO_DASHBOARD, keys: "g h" },
  { command: COMMAND_IDS.GOTO_PROFILE, keys: "g s" },
  { command: COMMAND_IDS.GOTO_THEMES, keys: "g e" },
  { command: COMMAND_IDS.GOTO_SHORTCUTS, keys: "g k" },
  { command: COMMAND_IDS.GOTO_LANGUAGE, keys: "g l" },

  // ── List navigation ────────────────────────────────────────────────────
  { command: COMMAND_IDS.LIST_NEXT, keys: "ArrowRight" },
  { command: COMMAND_IDS.LIST_NEXT, keys: "l" },
  { command: COMMAND_IDS.LIST_PREV, keys: "ArrowLeft" },
  { command: COMMAND_IDS.LIST_PREV, keys: "h" },

  // ── Focused-todo actions ───────────────────────────────────────────────
  { command: COMMAND_IDS.TODO_FOCUS_NEXT, keys: "ArrowDown", scope: "list" },
  { command: COMMAND_IDS.TODO_FOCUS_NEXT, keys: "j", scope: "list" },
  { command: COMMAND_IDS.TODO_FOCUS_PREV, keys: "ArrowUp", scope: "list" },
  { command: COMMAND_IDS.TODO_FOCUS_PREV, keys: "k", scope: "list" },
  { command: COMMAND_IDS.TODO_FOCUS_FIRST, keys: "Home", scope: "list" },
  { command: COMMAND_IDS.TODO_FOCUS_LAST, keys: "End", scope: "list" },
  { command: COMMAND_IDS.TODO_OPEN, keys: "Enter", scope: "list" },
  { command: COMMAND_IDS.TODO_CLEAR_FOCUS, keys: "Escape", scope: "list" },
  { command: COMMAND_IDS.TODO_TOGGLE_COMPLETE, keys: "Space", scope: "list" },
  { command: COMMAND_IDS.TODO_TOGGLE_COMPLETE, keys: "e", scope: "list" },
  { command: COMMAND_IDS.TODO_TOGGLE_IMPORTANT, keys: "i", scope: "list" },
  { command: COMMAND_IDS.TODO_TOGGLE_IMPORTANT, keys: "s", scope: "list" },
  { command: COMMAND_IDS.TODO_ADD_TO_MY_DAY, keys: "t", scope: "list" },
  { command: COMMAND_IDS.TODO_DELETE, keys: "Delete", scope: "list" },
  { command: COMMAND_IDS.TODO_DELETE, keys: "Backspace", scope: "list" },

  // ── Selection mode ─────────────────────────────────────────────────────
  { command: COMMAND_IDS.SELECTION_TOGGLE, keys: "x", scope: "list" },
  { command: COMMAND_IDS.SELECTION_SELECT_ALL, keys: "Mod+a", scope: "list" },
  // TODO_MOVE_UP/DOWN, TODO_DUPLICATE, TODO_MOVE_TO_LIST: no default binding yet
  // (see shortcuts-catalog.md "Reserved" section).

  // ── Drawer ──────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.DRAWER_CLOSE, keys: "Escape", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_DELETE, keys: "Delete", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_DELETE, keys: "Backspace", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_FOCUS_TITLE, keys: "c", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_DATE, keys: "t", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_REMINDER, keys: "r", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_RECURRENCE, keys: "p", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_NOTES, keys: "n", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_SUBTASK, keys: "y", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_OPEN_ATTACHMENT, keys: "a", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_TOGGLE_IMPORTANT, keys: "i", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_TOGGLE_IMPORTANT, keys: "s", scope: "todo-detail" },
  { command: COMMAND_IDS.DRAWER_SAVE_CLOSE, keys: "Mod+Enter", scope: "todo-detail" },
  // DRAWER_ADD_TO_MY_DAY, DRAWER_MOVE_TO_LIST: reserved, no default binding.

  // ── Dialogs ─────────────────────────────────────────────────────────────
  // NOTE: Dialog Escape is intentionally NOT bound globally. Kobalte's Dialog
  // primitive owns its own Escape behaviour (with focus trap restoration);
  // custom overlays bind locally via useBinding() while they're open.
  { command: COMMAND_IDS.DIALOG_CONFIRM, keys: "Enter", scope: "dialog" },
];

// ── Contributed by plugins, NOT part of the app-wide catalogue ──────────────
// PLUGIN-KERNEL: these are declared here so the i18n debt of this file keeps
// tracking them, but they are registered by the activity plugin
// (`kernel/composition.ts`). An app that cannot mount the feature no longer
// advertises the shortcut in the help modal.

export const UNDO_COMMAND: Command = {
  id: COMMAND_IDS.UNDO,
  category: "Global Actions",
  titleKey: "Undo last action",
  when: "!input.focused && !palette.open && !dialog.open",
};

export const UNDO_BINDING: KeyBinding = { command: COMMAND_IDS.UNDO, keys: "Mod+z" };
